import type { Editor as TEditor } from "@tiptap/core";
import { NodeSelection } from "@tiptap/pm/state";
import { GripVertical } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** Blocks a handle can grab: list items on their own (Evernote moves one bullet at a time), else top-level blocks. */
const ITEM = new Set(["listItem", "taskItem"]);

type Target = { pos: number; top: number; left: number };

/*
 * Evernote's block handle: hovering a paragraph, heading, list item, image… shows a ⠿ grip in the left
 * margin. Drag it to move the block; click it to select the block. Hidden while typing, back on mouse move.
 * Tables have their own grip (TableControls), so none here.
 */
export function BlockHandle({ editor }: { editor: TEditor }) {
  const [target, setTarget] = useState<Target | null>(null);
  const raf = useRef(0);
  const dragging = useRef(false);

  useEffect(() => {
    const view = editor.view;
    const scroller = (view.dom as HTMLElement).closest(".editor-scroll") as HTMLElement | null;
    // Hover-only, like Evernote desktop; phones (touch only) get none.
    if (!scroller || !window.matchMedia("(hover: hover)").matches) return;

    const find = (x: number, y: number): Target | null => {
      if (editor.isDestroyed || !editor.isEditable) return null;
      const dom = view.dom as HTMLElement;
      const box = dom.getBoundingClientRect();
      if (y < box.top || y > box.bottom) return null;
      // Hovering in the margin still finds the block at that height.
      const hit = view.posAtCoords({ left: Math.min(Math.max(x, box.left + 2), box.right - 2), top: y });
      if (!hit) return null;
      const $p = view.state.doc.resolve(hit.inside >= 0 ? hit.inside : hit.pos);
      let depth = 0;
      for (let d = $p.depth; d > 0; d--) {
        const n = $p.node(d);
        if (n.type.spec.tableRole) return null;
        if (ITEM.has(n.type.name)) {
          depth = d;
          break;
        }
      }
      let pos: number;
      if (depth) pos = $p.before(depth);
      else if ($p.depth >= 1) pos = $p.before(1);
      else {
        // Between blocks or on an atom (image, file, task): the node at that position.
        const node = view.state.doc.nodeAt(hit.inside >= 0 ? hit.inside : hit.pos);
        if (!node || node.type.name === "table") return null;
        pos = hit.inside >= 0 ? hit.inside : hit.pos;
      }
      const node = view.state.doc.nodeAt(pos);
      if (!node || node.type.name === "table") return null;
      const el = view.nodeDOM(pos) as HTMLElement | null;
      if (!el?.getBoundingClientRect) return null;
      const r = el.getBoundingClientRect();
      // Centre the grip on the block's first line.
      let lineTop = r.top;
      let lineH = 24;
      if (node.isTextblock || node.firstChild?.isTextblock) {
        try {
          const c = view.coordsAtPos(pos + (node.isTextblock ? 1 : 2));
          lineTop = c.top;
          lineH = c.bottom - c.top;
        } catch {
          /* empty block */
        }
      }
      const sc = scroller.getBoundingClientRect();
      const top = lineTop + lineH / 2 - 10;
      if (top < sc.top || top > sc.bottom - 20) return null;
      // A list item's grip sits left of its bullet or number, like Evernote's.
      const edge = ITEM.has(node.type.name) ? (el.parentElement?.getBoundingClientRect().left ?? r.left) : r.left;
      // A heading with a fold chevron (Collapsible sections) keeps the chevron clickable: the grip goes left of it.
      const gap = el.classList.contains("fold-head") ? 46 : 24;
      return { pos, top, left: Math.max(sc.left + 2, edge - gap) };
    };

    const onMove = (e: MouseEvent) => {
      if (dragging.current) return;
      cancelAnimationFrame(raf.current);
      const { clientX, clientY, target: t } = e;
      if ((t as Element).closest?.(".block-handle")) return;
      raf.current = requestAnimationFrame(() => setTarget(find(clientX, clientY)));
    };
    const hide = () => {
      if (dragging.current) return;
      cancelAnimationFrame(raf.current);
      setTarget(null);
    };
    const onLeave = (e: MouseEvent) => {
      if (!(e.relatedTarget as Element | null)?.closest?.(".block-handle")) hide();
    };
    scroller.addEventListener("mousemove", onMove);
    scroller.addEventListener("mouseleave", onLeave);
    scroller.addEventListener("scroll", hide, { passive: true });
    view.dom.addEventListener("keydown", hide);
    return () => {
      cancelAnimationFrame(raf.current);
      scroller.removeEventListener("mousemove", onMove);
      scroller.removeEventListener("mouseleave", onLeave);
      scroller.removeEventListener("scroll", hide);
      view.dom.removeEventListener("keydown", hide);
    };
  }, [editor]);

  if (!target) return null;
  const select = () => {
    const { state } = editor;
    if (target.pos >= state.doc.content.size) return null;
    const sel = NodeSelection.create(state.doc, target.pos);
    editor.view.dispatch(state.tr.setSelection(sel));
    return sel;
  };
  return createPortal(
    <button
      className="block-handle"
      style={{ top: target.top, left: target.left }}
      title="Drag to move · click to select"
      aria-label="Move block"
      draggable
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        select();
        editor.view.focus();
      }}
      onDragStart={(e) => {
        const sel = select();
        if (!sel) return;
        dragging.current = true;
        const el = editor.view.nodeDOM(target.pos) as HTMLElement | null;
        e.dataTransfer.effectAllowed = "move";
        if (el) {
          e.dataTransfer.setData("text/html", el.outerHTML);
          e.dataTransfer.setDragImage(el, 0, 0);
        }
        editor.view.dragging = { slice: sel.content(), move: true, node: sel } as typeof editor.view.dragging;
      }}
      onDragEnd={() => {
        dragging.current = false;
        setTarget(null);
      }}
    >
      <GripVertical size={14} />
    </button>,
    document.body,
  );
}
